import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronLeft, ChevronRight, Clock, User, Play, Trophy } from 'lucide-react';
import { formatDuration } from '../../../../utils/formatDuration';

const ITEM_WIDTH = 340;

const Top10Carrusel = ({ shows = [] }) => {
  const scrollContainerRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [showButtons, setShowButtons] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkOverflow = () => {
      const el = scrollContainerRef.current;
      if (el) {
        setShowButtons(el.scrollWidth > el.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [shows]);

  if (!shows.length) return null;

  const scroll = (dir) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -ITEM_WIDTH : ITEM_WIDTH, behavior: 'smooth' });
  };

  const adjustColor = (hex, amount) => {
    hex = (hex || '').replace('#', '');
    if (hex.length !== 6) return '#000000';
    let r = Math.max(0, Math.min(255, parseInt(hex.slice(0,2),16) + amount));
    let g = Math.max(0, Math.min(255, parseInt(hex.slice(2,4),16) + amount));
    let b = Math.max(0, Math.min(255, parseInt(hex.slice(4,6),16) + amount));
    return `#${((1<<24) + (r<<16) + (g<<8) + b).toString(16).slice(1)}`;
  };

  return (
    <div className="relative py-6">
      <div className="flex items-center justify-between mb-5 px-4">
        <h2 className="text-2xl font-bold">
          <span className="text-yellow-500 mr-2">|</span>
          <Trophy size={20} className="inline text-yellow-500 mr-2" />
          Top 10 Shows más populares
        </h2>
        {showButtons && (
          <div className="flex space-x-2">
            <button onClick={() => scroll('left')} className="bg-gray-800 hover:bg-gray-700 text-white rounded-full p-1.5">
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => scroll('right')} className="bg-gray-800 hover:bg-gray-700 text-white rounded-full p-1.5">
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      <div className="relative overflow-hidden">
        <div ref={scrollContainerRef}
             className="flex gap-5 pb-4 overflow-x-auto scroll-smooth px-4 no-scrollbar"
             style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
          {shows.slice(0,10).map((show, index) => {
            const isHovered = index === hoveredIndex;
            const imageSrc = show.imagen || `https://placehold.co/340x190?text=${show.titulo}`;
            const ranking = index + 1;
            return (
              <div key={show.videoShowId}
                   className="flex-shrink-0 w-[340px] relative rounded-lg overflow-hidden neon-border"
                   onMouseEnter={() => setHoveredIndex(index)}
                   onMouseLeave={() => setHoveredIndex(null)}>
                <div className="w-full h-[190px] bg-gray-900 overflow-hidden">
                  <img src={imageSrc} alt={show.titulo}
                       className={`w-full h-full object-cover transition-all duration-500 ${isHovered ? 'scale-110 brightness-75' : 'scale-100'}`} />
                  <div className="absolute top-2 right-2">
                    <div className={`${ranking <= 3 ? 'bg-gradient-to-r from-yellow-500 to-amber-600' : 'bg-gray-800'}
                                    text-white font-bold w-10 h-10 rounded-full shadow-lg flex items-center justify-center
                                    border-2 ${ranking===1?'border-yellow-300':ranking===2?'border-gray-300':ranking===3?'border-amber-700':'border-gray-700'}`}>
                      {ranking}
                    </div>
                  </div>
                  {show.tags?.length > 0 && (
                    <div className={`absolute top-2 left-2 flex flex-wrap gap-1 transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
                      {show.tags.slice(0,2).map((tag, i) => {
                        const color = tag.colorHex || '#EF4444';
                        return (
                          <div key={tag.tagId || i}
                               className="text-white text-xs font-bold px-2 py-1 rounded-full shadow-md border"
                               style={{ backgroundColor: color, borderColor: adjustColor(color, -20) }}>
                            {tag.nombre}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-medium text-lg truncate mb-1">{show.titulo}</h3>
                    <div className="flex items-center text-gray-300 text-sm">
                      {show.calificacionPromedio > 0 && (
                        <div className="flex items-center mr-3">
                          <Star className="text-yellow-400 mr-1" size={14} />
                          <span>{show.calificacionPromedio.toFixed(1)}</span>
                        </div>
                      )}
                      <div className="flex items-center mr-3">
                        <Clock className="mr-1" size={14} />
                        <span>{formatDuration(show.duracion)} min</span>
                      </div>
                      {show.comediantes?.length > 0 && (
                        <div className="flex items-center overflow-hidden">
                          <User className="mr-1" size={14} />
                          <span className="truncate">{show.comediantes.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                       onClick={() => navigate(`/contenido/ver/${show.videoShowId}`)}>
                    <button className="bg-red-600 hover:bg-red-700 text-white rounded-full p-4 shadow-lg transition-transform duration-300 hover:scale-110" aria-label="Reproducir">
                      <Play size={24} fill="white" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showButtons && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-900 to-transparent pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-900 to-transparent pointer-events-none" />
          </>
        )}
      </div>
    </div>
  );
};

export default Top10Carrusel;